import type {
    ActivityPage,
    Comparison,
    CopyOutcome,
    Environment,
    EnvironmentRef,
    ImportOutcome,
    ImportPlan,
    KeyIndexEntry,
    Person,
    RevealPurpose,
    Role,
    SectionDetail,
    SectionSummary,
    User,
    Variable,
    VariableVersion,
} from '@fleequid-secrets/shared';

// The only module that talks to the server. A change in a protected environment carries the environment the person
// confirmed (X-Confirm-Environment); values only ever arrive in the answers to reveal, version and export.

export class ApiError extends Error {
    constructor(
        readonly status: number,
        message: string,
        // What the server says to branch on: `confirm` (a protected environment), `stale`, `password`.
        readonly code?: string,
    ) {
        super(message);
    }
}

interface RequestOptions {
    method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
    body?: unknown;
    // The protected environment's slug, once the person confirmed the change.
    confirm?: string | null;
    signal?: AbortSignal;
}

// Told about every failed request: the shell signs out on a 401 and asks for a new password on `password`.
let listener: ((error: ApiError) => void) | null = null;
export const onApiError = (next: (error: ApiError) => void): void => {
    listener = next;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const response = await fetch(path, {
        method: options.method ?? 'GET',
        credentials: 'same-origin',
        headers: {
            ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
            ...(options.confirm ? { 'x-confirm-environment': options.confirm } : {}),
        },
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal,
    });
    if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { error?: string; code?: string };
        const error = new ApiError(response.status, body.error ?? response.statusText, body.code);
        listener?.(error);
        throw error;
    }

    return (await response.json()) as T;
}

const query = (values: Record<string, string | number | null | undefined>): string =>
    new URLSearchParams(
        Object.entries(values).flatMap(([key, value]) => (value === null || value === undefined || value === '' ? [] : [[key, String(value)]])),
    ).toString();

export interface EnvironmentInput {
    name: string;
    slug?: string;
    color: string;
    protected: boolean;
}

export interface ActivityFilter {
    environment?: string;
    section?: string;
    variable?: string;
    actor?: string;
    kind?: 'changes' | 'reads' | 'access';
}

export const api = {
    me: (): Promise<{ user: User }> => request('/api/auth/me'),
    login: (email: string, password: string): Promise<{ user: User }> => request('/api/auth/login', { method: 'POST', body: { email, password } }),
    logout: (): Promise<unknown> => request('/api/auth/logout', { method: 'POST' }),
    changePassword: (current: string, next: string): Promise<{ user: User }> =>
        request('/api/auth/password', { method: 'POST', body: { current, next } }),

    environments: (): Promise<{ environments: Environment[] }> => request('/api/environments'),
    createEnvironment: (input: EnvironmentInput): Promise<{ environment: EnvironmentRef }> =>
        request('/api/environments', { method: 'POST', body: input }),
    createDefaults: (): Promise<{ environments: EnvironmentRef[] }> => request('/api/environments/defaults', { method: 'POST' }),
    updateEnvironment: (id: string, patch: Partial<EnvironmentInput>, confirm: string | null): Promise<{ environment: EnvironmentRef }> =>
        request(`/api/environments/${id}`, { method: 'PATCH', body: patch, confirm }),
    reorderEnvironments: (ids: string[]): Promise<unknown> => request('/api/environments/order', { method: 'POST', body: { ids } }),
    deleteEnvironment: (id: string, typed: string): Promise<unknown> =>
        request(`/api/environments/${id}`, { method: 'DELETE', body: { confirm: typed } }),

    createSection: (
        environmentId: string,
        input: { name: string; description?: string; everywhere?: boolean },
    ): Promise<{ section: SectionSummary }> => request(`/api/environments/${environmentId}/sections`, { method: 'POST', body: input }),
    section: (id: string, signal?: AbortSignal): Promise<SectionDetail> => request(`/api/sections/${id}`, { signal }),
    updateSection: (id: string, patch: { name?: string; description?: string }, confirm: string | null): Promise<{ section: SectionSummary }> =>
        request(`/api/sections/${id}`, { method: 'PATCH', body: patch, confirm }),
    deleteSection: (id: string, typed: string, confirm: string | null): Promise<unknown> =>
        request(`/api/sections/${id}`, { method: 'DELETE', body: { confirm: typed }, confirm }),
    copySection: (id: string, environmentId: string, values: boolean, confirm: string | null): Promise<CopyOutcome & { section: SectionSummary }> =>
        request(`/api/sections/${id}/copy`, { method: 'POST', body: { environmentId, values }, confirm }),

    createVariable: (
        sectionId: string,
        input: { key: string; value: string; note: string },
        confirm: string | null,
    ): Promise<{ variable: Variable }> => request(`/api/sections/${sectionId}/variables`, { method: 'POST', body: input, confirm }),
    updateVariable: (
        id: string,
        patch: { key?: string; value?: string; note?: string; baseVersion?: number },
        confirm: string | null,
    ): Promise<{ variable: Variable }> => request(`/api/variables/${id}`, { method: 'PATCH', body: patch, confirm }),
    deleteVariable: (id: string, confirm: string | null): Promise<unknown> => request(`/api/variables/${id}`, { method: 'DELETE', confirm }),
    restoreVariable: (id: string, confirm: string | null): Promise<{ variable: Variable }> =>
        request(`/api/variables/${id}/restore`, { method: 'POST', confirm }),
    reveal: (sectionId: string, ids: string[] | null, purpose: RevealPurpose): Promise<{ values: Record<string, string> }> =>
        request(`/api/sections/${sectionId}/reveal`, { method: 'POST', body: { ids: ids ?? undefined, purpose } }),
    versions: (id: string): Promise<{ versions: VariableVersion[] }> => request(`/api/variables/${id}/versions`),
    revealVersion: (id: string, version: number, purpose: RevealPurpose): Promise<{ value: string }> =>
        request(`/api/variables/${id}/versions/${version}/reveal`, { method: 'POST', body: { purpose } }),
    restoreVersion: (id: string, version: number, confirm: string | null): Promise<{ variable: Variable }> =>
        request(`/api/variables/${id}/versions/${version}/restore`, { method: 'POST', confirm }),

    planImport: (sectionId: string, content: string, signal?: AbortSignal): Promise<{ plan: ImportPlan }> =>
        request(`/api/sections/${sectionId}/import`, { method: 'POST', body: { content }, signal }),
    applyImport: (
        sectionId: string,
        content: string,
        options: { overwrite: boolean; prune: boolean },
        confirm: string | null,
    ): Promise<{ outcome: ImportOutcome }> =>
        request(`/api/sections/${sectionId}/import`, { method: 'POST', body: { content, apply: true, ...options }, confirm }),
    exportSection: (sectionId: string): Promise<{ filename: string; content: string; count: number }> =>
        request(`/api/sections/${sectionId}/export`, { method: 'POST' }),

    keys: (): Promise<{ keys: KeyIndexEntry[] }> => request('/api/keys'),
    compare: (slug: string): Promise<Comparison> => request(`/api/compare?${query({ slug })}`),
    activity: (filter: ActivityFilter & { before?: string | null; limit?: number }): Promise<ActivityPage> =>
        request(`/api/activity?${query({ ...filter, before: filter.before ?? null })}`),

    people: (): Promise<{ users: Person[] }> => request('/api/users'),
    addPerson: (input: { email: string; name: string; role: Role }): Promise<{ user: Person; password: string }> =>
        request('/api/users', { method: 'POST', body: input }),
    updatePerson: (id: string, patch: { name?: string; role?: Role }): Promise<{ user: Person }> =>
        request(`/api/users/${id}`, { method: 'PATCH', body: patch }),
    resetPassword: (id: string): Promise<{ password: string }> => request(`/api/users/${id}/password`, { method: 'POST' }),
    removePerson: (id: string): Promise<unknown> => request(`/api/users/${id}`, { method: 'DELETE' }),
};

export const messageOf = (error: unknown): string => (error instanceof ApiError ? error.message : 'The server did not answer');
